import {
  aclSetSchema,
  addItemSchema,
  authConfigSchema,
  barcodeLookupResponseSchema,
  bulkDeleteItemsResponseSchema,
  bulkMoveContainerResultSchema,
  bulkMoveContainersSchema,
  bulkMoveItemResultSchema,
  bulkMoveItemsSchema,
  catalogCreateSchema,
  catalogPatchSchema,
  catalogRootAssignSchema,
  containerCreateSchema,
  containerEntryResponseSchema,
  containerItemLookupResponseSchema,
  containerItemResponseSchema,
  containerItemsResponseSchema,
  containerSchema,
  containerUpdateSchema,
  deleteContainerSchema,
  EMPTY_ITEM_FACETS,
  grantLevelSchema,
  itemCategorySchema,
  itemContainerSchema,
  itemFacetsSchema,
  itemPhotoSchema,
  itemRefreshResponseSchema,
  itemSchema,
  itemsSyncResponseSchema,
  linkChildSchema,
  loginResponseSchema,
  manualTextInputSchema,
  meResponseSchema,
  moveContainerSchema,
  moveResultSchema,
  photosResponseSchema,
  propertiesSchema,
  purgeContainersSchema,
  reExtractResponseSchema,
  rePreviewResponseSchema,
  reTranscribeResponseSchema,
  reorderItemPhotosSchema,
  restoreContainerSchema,
  searchResponseSchema,
  unlinkContainerSchema,
  updateItemSchema,
  updateQuantitySchema,
  z,
  type AuthConfig,
  type BarcodeLookupResponse,
  type BulkDeleteItemsResponse,
  type BulkMoveContainerResult,
  type BulkMoveContainersInput,
  type BulkMoveItemResult,
  type BulkMoveItemsInput,
  type Container,
  type ContainerEntryResponse,
  type ContainerInput,
  type ContainerItemLookupResponse,
  type ContainerItemResponse as ContainerItemResponsePayload,
  type DeadlineFilter,
  type FileKind,
  type FileLocation,
  type GrantLevel,
  type Item,
  type ItemContainer,
  type ItemPhoto,
  type ItemPhotoType,
  type ItemRefreshResponse,
  type ItemSort,
  type ItemsSyncResponse,
  type MoveResult,
  type PhotosResponse,
  type Properties,
  type ReExtractResponse,
  type RePreviewResponse,
  type ReTranscribeResponse,
  type SearchResult,
  type SessionIdentity,
} from "@egalregal/shared";
import { InvalidInputError, InvalidResponseError, UnreachableError, apiErrorFor } from "./errors";
import type { Transport } from "./transport";

/**
 * The grouped API client built on a transport (request-client-seam 01).
 *
 * Every request shares one path through `request` / `requestVoid` /
 * `requestMultipart`: check `ok` before reading a body, read JSON only when the
 * content type says JSON, validate against a schema, and throw one typed error
 * vocabulary. The six capability groups below are the only way a feature
 * reaches the network (request-client-seam 03).
 *
 * `credentials` is deliberately never set: every request is same-origin, and
 * the one default is "the browser's default".
 */

/**
 * One JSON request. The `ok` check comes first: a failing status throws the
 * typed error with its parsed envelope, and only a successful response is
 * validated against `schema`.
 */
export async function request<S extends z.ZodTypeAny>(
  transport: Transport,
  path: string,
  schema: S,
  init?: RequestInit,
): Promise<z.output<S>> {
  const res = await send(transport, path, init);
  if (!res.ok) throw apiErrorFor(res.status, await readErrorBody(res));

  const parsed = schema.safeParse(await readSuccessBody(res, path));
  if (!parsed.success) {
    throw new InvalidResponseError(path, { cause: parsed.error });
  }
  return parsed.data;
}

/** A request with no response payload (204, logout, a bare delete). */
export async function requestVoid(
  transport: Transport,
  path: string,
  init?: RequestInit,
): Promise<void> {
  const res = await send(transport, path, init);
  if (!res.ok) throw apiErrorFor(res.status, await readErrorBody(res));
}

/** Multipart goes through the same send/parse path; only the body differs. */
export async function requestMultipart<S extends z.ZodTypeAny>(
  transport: Transport,
  path: string,
  form: FormData,
  schema: S,
  init?: Omit<RequestInit, "body">,
): Promise<z.output<S>> {
  // The browser sets the multipart boundary; a caller-supplied content-type
  // would break it, so it is dropped rather than passed through.
  const headers = new Headers(init?.headers);
  headers.delete("content-type");

  return request(transport, path, schema, {
    ...init,
    method: init?.method ?? "POST",
    headers,
    body: form,
  });
}

// ── Containers ──────────────────────────────────────────────────────

/** One file travelling with a new container (multipart, same request). */
export interface ContainerFileUpload {
  file: File;
  kind: FileKind;
  location?: FileLocation | null;
}

export interface ContainersRequests {
  /** Create a container; an optional id becomes its identity (a 409 if taken). */
  create(input: ContainerInput, id?: string, opts?: { entryId?: string }): Promise<Container>;
  /** Create a container together with its files in one multipart request. */
  createWithFiles(
    input: ContainerInput,
    files: readonly ContainerFileUpload[],
    id?: string,
    opts?: { entryId?: string },
  ): Promise<Container>;
  /** All non-deleted containers — full visible union, or one catalog entry. */
  list(entryId?: string): Promise<Container[]>;
  get(id: string): Promise<Container>;
  /** The catalog entry a container belongs to, read-enforced like the read. */
  entry(containerId: string, currentEntryId?: string): Promise<ContainerEntryResponse>;
  /** PATCH the scalar fields, carrying the version the form was built from. */
  update(id: string, input: ContainerInput, version: number): Promise<Container>;
  /** Soft-delete, carrying the version the delete decision was built from. */
  delete(id: string, version: number, confirmChildren?: boolean): Promise<Container>;
  /** Restore a soft-deleted container from the trash. */
  restore(id: string, version: number): Promise<Container>;
  /** All soft-deleted containers (the trash list). */
  trash(): Promise<Container[]>;
  /** Permanently delete the given trashed containers; returns the count. */
  purge(ids: string[]): Promise<number>;
  /** Link an existing container as a child of another. */
  linkChild(parentId: string, childId: string): Promise<Container>;
  /** Detach a container from its current parent. */
  unlinkFromParent(id: string, version: number): Promise<Container>;
  /** Move a container under a new parent, or to root when `parentId` is null. */
  move(
    id: string,
    parentId: string | null,
    version: number,
    entryId?: string,
    confirmTransfer?: boolean,
  ): Promise<MoveResult>;
  /** Move a batch of containers to one destination entry (all-or-nothing). */
  bulkMove(input: BulkMoveContainersInput): Promise<BulkMoveContainerResult>;
  /** Trigger AI re-extraction for a container. */
  reExtract(id: string): Promise<ReExtractResponse>;
}

function createContainersRequests(transport: Transport): ContainersRequests {
  return {
    create: async (input, id, opts) =>
      request(
        transport,
        "/api/containers",
        containerSchema,
        jsonInit(
          "POST",
          checkedBody(
            containerCreateSchema,
            { ...input, id, entryId: opts?.entryId },
            "containers.create received an invalid container input",
          ),
        ),
      ),

    createWithFiles: async (input, files, id, opts) => {
      // Validate the scalar fields exactly like the JSON create; the multipart
      // fields themselves stay as the caller wrote them.
      const parsed = containerCreateSchema.safeParse({ ...input, id, entryId: opts?.entryId });
      if (!parsed.success) {
        throw new InvalidInputError("containers.createWithFiles received an invalid container input", {
          cause: parsed.error,
        });
      }

      const form = new FormData();
      form.append("kind", input.kind);
      form.append("name", input.name);
      if (input.description) form.append("description", input.description);
      if (input.owner) form.append("owner", input.owner);
      if (input.tags && input.tags.length > 0) form.append("tags", JSON.stringify(input.tags));
      if (input.properties && Object.keys(input.properties).length > 0)
        form.append("properties", JSON.stringify(input.properties));
      if (id) form.append("id", id);
      if (opts?.entryId) form.append("entryId", opts.entryId);

      for (const f of files) {
        form.append("files", f.file);
        form.append("fileKind", f.kind);
      }

      return requestMultipart(transport, "/api/containers", form, containerSchema);
    },

    list: async (entryId) => {
      const query = entryId ? `?entry=${encodeURIComponent(entryId)}` : "";
      return request(transport, `/api/containers${query}`, z.array(containerSchema));
    },

    get: async (id) => request(transport, `/api/containers/${id}`, containerSchema),

    entry: async (containerId, currentEntryId) => {
      const query = currentEntryId ? `?current=${encodeURIComponent(currentEntryId)}` : "";
      return request(
        transport,
        `/api/containers/${containerId}/entry${query}`,
        containerEntryResponseSchema,
      );
    },

    update: async (id, input, version) =>
      request(
        transport,
        `/api/containers/${id}`,
        containerSchema,
        jsonInit(
          "PATCH",
          checkedBody(
            containerUpdateSchema,
            { ...input, version },
            "containers.update received an invalid container update",
          ),
        ),
      ),

    delete: async (id, version, confirmChildren = false) =>
      request(
        transport,
        `/api/containers/${id}`,
        containerSchema,
        jsonInit(
          "DELETE",
          checkedBody(
            deleteContainerSchema,
            confirmChildren ? { version, confirmChildren } : { version },
            "containers.delete received an invalid delete input",
          ),
        ),
      ),

    restore: async (id, version) =>
      request(
        transport,
        `/api/containers/${id}/restore`,
        containerSchema,
        jsonInit(
          "POST",
          checkedBody(
            restoreContainerSchema,
            { version },
            "containers.restore received an invalid restore input",
          ),
        ),
      ),

    trash: async () => request(transport, "/api/trash", z.array(containerSchema)),

    purge: async (ids) => {
      const body = checkedBody(purgeContainersSchema, { ids }, "containers.purge received an invalid purge input");
      const result = await request(
        transport,
        "/api/trash/purge",
        z.object({ purged: z.number() }),
        jsonInit("POST", body),
      );
      return result.purged;
    },

    linkChild: async (parentId, childId) =>
      request(
        transport,
        `/api/containers/${parentId}/children`,
        containerSchema,
        jsonInit(
          "POST",
          checkedBody(linkChildSchema, { childId }, "containers.linkChild received an invalid child id"),
        ),
      ),

    unlinkFromParent: async (id, version) =>
      request(
        transport,
        `/api/containers/${id}/parent`,
        containerSchema,
        jsonInit(
          "DELETE",
          checkedBody(
            unlinkContainerSchema,
            { version },
            "containers.unlinkFromParent received an invalid delete input",
          ),
        ),
      ),

    move: async (id, parentId, version, entryId, confirmTransfer) =>
      request(
        transport,
        `/api/containers/${id}/parent`,
        moveResultSchema,
        jsonInit(
          "PUT",
          checkedBody(
            moveContainerSchema,
            {
              parentId,
              version,
              ...(entryId !== undefined ? { entryId } : {}),
              ...(confirmTransfer ? { confirmTransfer: true } : {}),
            },
            "containers.move received an invalid move input",
          ),
        ),
      ),

    bulkMove: async (input) =>
      request(
        transport,
        "/api/containers/bulk-move",
        bulkMoveContainerResultSchema,
        jsonInit(
          "POST",
          checkedBody(bulkMoveContainersSchema, input, "containers.bulkMove received an invalid move input"),
        ),
      ),

    reExtract: async (id) =>
      request(transport, `/api/containers/${id}/re-extract`, reExtractResponseSchema, {
        method: "POST",
      }),
  };
}

// ── Items ───────────────────────────────────────────────────────────

/** Item-list query parameters (filter-table-reuse 02). */
export interface ListAllItemsParams {
  q?: string;
  sort?: ItemSort;
  page?: number;
  limit?: number;
  /** Server-side Overdue / Expiring-soon filter; repeated `deadline` params. */
  deadline?: readonly DeadlineFilter[];
  /** Property filters: OR within a param, AND across. */
  owners?: readonly string[];
  tags?: readonly string[];
  categories?: readonly string[];
}

/** One page of `GET /api/items`: rows, totals, the deadline counts and facets. */
const itemListResponseSchema = z.object({
  items: z.array(itemSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  counts: z.object({ overdue: z.number(), soon: z.number() }),
  facets: itemFacetsSchema.default(EMPTY_ITEM_FACETS),
});

export type ItemListResponse = z.infer<typeof itemListResponseSchema>;

/** `POST /api/items` body: the standalone-item fields the form collects. */
const itemCreateSchema = z.object({
  barcode: z.string().min(1),
  barcodeType: z.string().optional(),
  name: z.string().min(1),
  category: itemCategorySchema.optional(),
  photo: z.string().url().optional(),
  metadata: z.record(z.unknown()).optional(),
  properties: propertiesSchema.optional(),
});

export type ItemCreateInput = z.infer<typeof itemCreateSchema>;
/** `PATCH /api/items/:id` body; `metadata`/`properties` replace wholesale. */
export type ItemPatch = z.infer<typeof updateItemSchema>;
/** One item plus each container it sits in, with the container's display path. */
export type ItemDetailResponse = { item: Item; containers: ItemContainer[] };

const refreshItemsBodySchema = z.object({ ids: z.array(z.string().uuid()).min(1).max(10) });
const bulkDeleteItemsBodySchema = z.object({ ids: z.array(z.string().uuid()).min(1) });

/**
 * The placement shape the item views consume. The wire shape is
 * `ContainerItemResponsePayload` (the shared schema, where `description` is
 * defaulted); this narrower view is the historical frontend type and is kept
 * here, next to the capability that returns the payload, so consumers and
 * fixtures that predate the shared schema still type-check unchanged.
 */
export interface ContainerItemResponse {
  containerId: string;
  itemId: string;
  quantity: number;
  addedAt: string;
  item: {
    id: string;
    barcode: string;
    barcodeType: string;
    name: string;
    category: string;
    photo: string | null;
    photos: ItemPhoto[];
    metadata: Record<string, unknown>;
    properties?: Properties;
    createdAt: string;
  };
}

export interface ItemsRequests {
  /** Every placement in one container, each with its stored item. */
  list(containerId: string): Promise<ContainerItemResponsePayload[]>;
  /** Place a barcode in a container (creates the item when it is unknown). */
  add(containerId: string, barcode: string, quantity?: number): Promise<ContainerItemResponsePayload>;
  /** Set one placement's absolute total. */
  setQuantity(containerId: string, itemId: string, quantity: number): Promise<ContainerItemResponsePayload>;
  /** Remove one placement; the item itself stays. */
  remove(containerId: string, itemId: string): Promise<void>;
  /** Provider preview for a barcode no stored item matches (404 when unknown). */
  lookupBarcode(barcode: string): Promise<BarcodeLookupResponse>;
  /** Container-scoped lookup: `external: false` skips the provider step. */
  lookupInContainer(
    containerId: string,
    barcode: string,
    opts?: { external?: boolean },
  ): Promise<ContainerItemLookupResponse>;
  /** Full item + placement mirror for local search. */
  sync(): Promise<ItemsSyncResponse>;
  /** One page of the item list with counts and facets. */
  listAll(params: ListAllItemsParams): Promise<ItemListResponse>;
  /** Create a standalone item; idempotent by barcode (returns the existing one). */
  create(data: ItemCreateInput): Promise<Item>;
  /** One item with each container it sits in, plus that container's path. */
  get(itemId: string): Promise<ItemDetailResponse>;
  /** Edit an item's details; `metadata`/`properties` are replaced wholesale. */
  update(itemId: string, patch: ItemPatch): Promise<Item>;
  /** Permanently delete one item. */
  delete(itemId: string): Promise<void>;
  /** Re-extract metadata for the given items (max 10 per call). */
  refresh(ids: string[]): Promise<ItemRefreshResponse>;
  /** Permanently delete the given items. */
  bulkDelete(ids: string[]): Promise<BulkDeleteItemsResponse>;
  /** Move placements out of one container in an all-or-nothing batch. */
  bulkMove(containerId: string, input: BulkMoveItemsInput): Promise<BulkMoveItemResult>;
  /** Upload an item photo; multipart, the browser sets the boundary. */
  addPhoto(
    itemId: string,
    file: File,
    type?: ItemPhotoType,
    location?: FileLocation | null,
  ): Promise<ItemPhoto>;
  /** Remove a photo from an item. */
  removePhoto(itemId: string, photoId: string): Promise<void>;
  /** Reorder an item's photos (first position = cover); returns the item. */
  reorderPhotos(itemId: string, ids: string[]): Promise<Item>;
}

function createItemsRequests(transport: Transport): ItemsRequests {
  return {
    list: async (containerId) =>
      request(transport, `/api/containers/${containerId}/items`, containerItemsResponseSchema),

    add: async (containerId, barcode, quantity = 1) =>
      request(
        transport,
        `/api/containers/${containerId}/items`,
        containerItemResponseSchema,
        jsonInit(
          "POST",
          checkedBody(addItemSchema, { barcode, quantity }, "items.add received an invalid add input"),
        ),
      ),

    setQuantity: async (containerId, itemId, quantity) =>
      request(
        transport,
        `/api/containers/${containerId}/items/${itemId}`,
        containerItemResponseSchema,
        jsonInit(
          "PATCH",
          checkedBody(updateQuantitySchema, { quantity }, "items.setQuantity received an invalid quantity"),
        ),
      ),

    remove: async (containerId, itemId) =>
      requestVoid(transport, `/api/containers/${containerId}/items/${itemId}`, {
        method: "DELETE",
      }),

    lookupBarcode: async (barcode) =>
      request(
        transport,
        `/api/items/lookup/${encodeURIComponent(barcode)}`,
        barcodeLookupResponseSchema,
      ),

    lookupInContainer: async (containerId, barcode, opts = {}) => {
      const query = opts.external === false ? "?external=0" : "";
      return request(
        transport,
        `/api/containers/${containerId}/items/lookup/${encodeURIComponent(barcode)}${query}`,
        containerItemLookupResponseSchema,
      );
    },

    sync: async () => request(transport, "/api/items/sync", itemsSyncResponseSchema),

    listAll: async (params) => {
      const qs = new URLSearchParams();
      if (params.q) qs.set("q", params.q);
      if (params.sort) qs.set("sort", params.sort);
      if (params.page !== undefined) qs.set("page", String(params.page));
      if (params.limit !== undefined) qs.set("limit", String(params.limit));
      for (const filter of params.deadline ?? []) qs.append("deadline", filter);
      for (const owner of params.owners ?? []) qs.append("owner", owner);
      for (const tag of params.tags ?? []) qs.append("tag", tag);
      for (const category of params.categories ?? []) qs.append("category", category);
      const query = qs.toString();
      return request(
        transport,
        query ? `/api/items?${query}` : "/api/items",
        itemListResponseSchema,
      );
    },

    create: async (data) =>
      request(
        transport,
        "/api/items",
        itemSchema,
        jsonInit("POST", checkedBody(itemCreateSchema, data, "items.create received an invalid item")),
      ),

    get: async (itemId) =>
      request(
        transport,
        `/api/items/${itemId}`,
        z.object({ item: itemSchema, containers: z.array(itemContainerSchema) }),
      ),

    update: async (itemId, patch) =>
      request(
        transport,
        `/api/items/${itemId}`,
        itemSchema,
        jsonInit(
          "PATCH",
          checkedBody(updateItemSchema, patch, "items.update received an invalid item update"),
        ),
      ),

    delete: async (itemId) => requestVoid(transport, `/api/items/${itemId}`, { method: "DELETE" }),

    refresh: async (ids) =>
      request(
        transport,
        "/api/items/refresh",
        itemRefreshResponseSchema,
        jsonInit(
          "POST",
          checkedBody(refreshItemsBodySchema, { ids }, "items.refresh received invalid item ids"),
        ),
      ),

    bulkDelete: async (ids) =>
      request(
        transport,
        "/api/items/bulk-delete",
        bulkDeleteItemsResponseSchema,
        jsonInit(
          "POST",
          checkedBody(bulkDeleteItemsBodySchema, { ids }, "items.bulkDelete received invalid item ids"),
        ),
      ),

    bulkMove: async (containerId, input) =>
      request(
        transport,
        `/api/containers/${containerId}/items/bulk-move`,
        bulkMoveItemResultSchema,
        jsonInit(
          "POST",
          checkedBody(bulkMoveItemsSchema, input, "items.bulkMove received an invalid move input"),
        ),
      ),

    addPhoto: async (itemId, file, type = "general", location) => {
      const form = new FormData();
      form.append("file", file);
      form.append("type", type);
      if (location) {
        form.append("latitude", String(location.latitude));
        form.append("longitude", String(location.longitude));
        form.append("capturedAt", location.capturedAt);
      }
      return requestMultipart(transport, `/api/items/${itemId}/photos`, form, itemPhotoSchema);
    },

    removePhoto: async (itemId, photoId) =>
      requestVoid(transport, `/api/items/${itemId}/photos/${photoId}`, { method: "DELETE" }),

    reorderPhotos: async (itemId, ids) =>
      request(
        transport,
        `/api/items/${itemId}/photos`,
        itemSchema,
        jsonInit(
          "PATCH",
          checkedBody(reorderItemPhotosSchema, { ids }, "items.reorderPhotos received invalid photo ids"),
        ),
      ),
  };
}

// ── Media ───────────────────────────────────────────────────────────

export interface MediaRequests {
  /** URL of a file's original, streamed by the API. */
  fileUrl(containerId: string, fileId: string): string;
  /** Downscaled JPEG preview; originals stream only on demand. */
  previewUrl(containerId: string, fileId: string): string;
  /** Original item photo (mirror of `fileUrl`). */
  itemPhotoUrl(itemId: string, photoId: string): string;
  /** Downscaled JPEG preview of an item photo (mirror of `previewUrl`). */
  itemPhotoPreviewUrl(itemId: string, photoId: string): string;
  /** Upload one file entry; multipart, the browser sets the boundary. */
  upload(
    containerId: string,
    file: File,
    kind: FileKind,
    version: number,
    location?: FileLocation | null,
  ): Promise<Container>;
  /** Remove a file (the original moves to the container's .trash/). */
  remove(containerId: string, fileId: string, version: number): Promise<Container>;
  /** Re-transcribe an audio file (deletes stt to trigger the CDC re-run). */
  reTranscribe(containerId: string, fileId: string, version: number): Promise<ReTranscribeResponse>;
  /** Re-generate a photo preview (deletes preview to trigger the CDC re-run). */
  rePreview(containerId: string, fileId: string, version: number): Promise<RePreviewResponse>;
  /** Replace an audio file's manual transcript; no version, no version bump. */
  setManualText(containerId: string, fileId: string, text: string): Promise<Container>;
  /** Revert an audio file to its auto transcript. */
  clearManualText(containerId: string, fileId: string): Promise<Container>;
}

function createMediaRequests(transport: Transport): MediaRequests {
  return {
    upload: async (containerId, file, kind, version, location) => {
      const form = new FormData();
      form.append("kind", kind);
      form.append("version", String(version));
      if (location) {
        form.append("latitude", String(location.latitude));
        form.append("longitude", String(location.longitude));
        form.append("capturedAt", location.capturedAt);
      }
      form.append("file", file);
      return requestMultipart(transport, `/api/containers/${containerId}/files`, form, containerSchema);
    },

    remove: async (containerId, fileId, version) =>
      request(
        transport,
        `/api/containers/${containerId}/files/${fileId}`,
        containerSchema,
        jsonInit(
          "DELETE",
          checkedBody(versionBodySchema, { version }, "media.remove received an invalid version"),
        ),
      ),

    reTranscribe: async (containerId, fileId, version) =>
      request(
        transport,
        `/api/containers/${containerId}/files/${fileId}/stt`,
        reTranscribeResponseSchema,
        jsonInit(
          "DELETE",
          checkedBody(versionBodySchema, { version }, "media.reTranscribe received an invalid version"),
        ),
      ),

    rePreview: async (containerId, fileId, version) =>
      request(
        transport,
        `/api/containers/${containerId}/files/${fileId}/preview`,
        rePreviewResponseSchema,
        jsonInit(
          "DELETE",
          checkedBody(versionBodySchema, { version }, "media.rePreview received an invalid version"),
        ),
      ),

    setManualText: async (containerId, fileId, text) =>
      request(
        transport,
        `/api/containers/${containerId}/files/${fileId}/manual-text`,
        containerSchema,
        jsonInit(
          "PUT",
          checkedBody(manualTextInputSchema, { text }, "media.setManualText received an invalid text"),
        ),
      ),

    clearManualText: async (containerId, fileId) =>
      request(
        transport,
        `/api/containers/${containerId}/files/${fileId}/manual-text`,
        containerSchema,
        { method: "DELETE" },
      ),

    fileUrl: (containerId, fileId) => `/api/containers/${containerId}/files/${fileId}`,

    previewUrl: (containerId, fileId) => `/api/containers/${containerId}/files/${fileId}/preview`,

    itemPhotoUrl: (itemId, photoId) => `/api/items/${itemId}/photos/${photoId}`,

    itemPhotoPreviewUrl: (itemId, photoId) => `/api/items/${itemId}/photos/${photoId}/preview`,
  };
}

// ── Catalogs ────────────────────────────────────────────────────────

/** One grant row on a catalog entry. */
export const catalogGrantSchema = z.object({ grantee: z.string(), level: grantLevelSchema });
export type CatalogGrant = z.infer<typeof catalogGrantSchema>;

/** An own entry as listed by GET /api/catalogs. */
export const ownCatalogEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  level: z.literal("owner"),
  /** Root container ids; membership is these roots plus their subtrees. */
  roots: z.array(z.string()),
  acl: z.array(catalogGrantSchema),
});
export type OwnCatalogEntry = z.infer<typeof ownCatalogEntrySchema>;

/** An entry another owner shared with me. */
export const sharedCatalogEntrySchema = z.object({
  owner: z.string(),
  /** `roots` is optional so a client tolerates a server that predates it. */
  entry: z.object({
    id: z.string(),
    name: z.string(),
    isDefault: z.boolean(),
    roots: z.array(z.string()).optional(),
  }),
  level: grantLevelSchema,
});
export type SharedCatalogEntry = z.infer<typeof sharedCatalogEntrySchema>;

/** Own entries + entries shared with the caller. */
export const catalogListSchema = z.object({
  entries: z.array(ownCatalogEntrySchema),
  sharedWithMe: z.array(sharedCatalogEntrySchema),
});
export type CatalogList = z.infer<typeof catalogListSchema>;

/** A catalog entry as returned by POST/PATCH. */
export const catalogEntryDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  isDefault: z.boolean(),
  roots: z.array(z.string()),
  createdAt: z.string(),
  acl: z.array(catalogGrantSchema),
});
export type CatalogEntryDetail = z.infer<typeof catalogEntryDetailSchema>;

/** `DELETE /api/catalogs/:id`: the entry's id and how many containers it trashed. */
const catalogDeleteResponseSchema = z.object({ id: z.string(), trashed: z.number() });
export type CatalogDeleteResponse = z.infer<typeof catalogDeleteResponseSchema>;

/** `PUT`/`DELETE /api/catalogs/:id/acl/:grantee`: the entry's acl after the write. */
const catalogAclResponseSchema = z.object({ id: z.string(), acl: z.array(catalogGrantSchema) });
export type CatalogAclResponse = z.infer<typeof catalogAclResponseSchema>;

export interface CatalogsRequests {
  /** Own entries + entries shared with the caller. */
  list(): Promise<CatalogList>;
  /** Owner-only: create a new catalog entry. */
  create(name: string): Promise<CatalogEntryDetail>;
  /** Owner-only: rename / set-default / replace roots of a catalog entry. */
  patch(id: string, patch: z.input<typeof catalogPatchSchema>): Promise<CatalogEntryDetail>;
  /** Owner-only: delete a catalog entry (its reachable containers go to the trash). */
  delete(id: string): Promise<CatalogDeleteResponse>;
  /**
   * Move a container into an entry's membership. A same-home reassignment
   * returns the entry; a cross-home assignment is an ownership transfer and
   * returns the moved container plus its dropped-placement count.
   */
  assignContainer(
    entryId: string,
    containerId: string,
    opts?: { version?: number; confirmTransfer?: boolean },
  ): Promise<CatalogEntryDetail | MoveResult>;
  /** Owner-only: grant/share a catalog entry with `grantee` at `level`. */
  setGrant(id: string, grantee: string, level: GrantLevel): Promise<CatalogAclResponse>;
  /** Owner-only: revoke `grantee`'s access to a catalog entry. */
  revokeGrant(id: string, grantee: string): Promise<CatalogAclResponse>;
  /** Owner-only: every username in users.json (share-dialog autocomplete). */
  usernames(): Promise<string[]>;
}

function createCatalogsRequests(transport: Transport): CatalogsRequests {
  return {
    list: async () => request(transport, "/api/catalogs", catalogListSchema),

    create: async (name) =>
      request(
        transport,
        "/api/catalogs",
        catalogEntryDetailSchema,
        jsonInit(
          "POST",
          checkedBody(catalogCreateSchema, { name }, "catalogs.create received an invalid name"),
        ),
      ),

    patch: async (id, patch) =>
      request(
        transport,
        `/api/catalogs/${id}`,
        catalogEntryDetailSchema,
        jsonInit(
          "PATCH",
          checkedBody(catalogPatchSchema, patch, "catalogs.patch received an invalid catalog patch"),
        ),
      ),

    delete: async (id) =>
      request(transport, `/api/catalogs/${id}`, catalogDeleteResponseSchema, { method: "DELETE" }),

    assignContainer: async (entryId, containerId, opts = {}) => {
      const body: { version?: number; confirmTransfer?: boolean } = {};
      if (opts.version !== undefined) body.version = opts.version;
      if (opts.confirmTransfer !== undefined) body.confirmTransfer = opts.confirmTransfer;
      // No confirmation fields means no body at all, not an empty object.
      const init: RequestInit =
        Object.keys(body).length === 0
          ? { method: "PUT" }
          : jsonInit(
              "PUT",
              checkedBody(
                catalogRootAssignSchema,
                body,
                "catalogs.assignContainer received an invalid assignment",
              ),
            );
      return request(
        transport,
        `/api/catalogs/${encodeURIComponent(entryId)}/roots/${encodeURIComponent(containerId)}`,
        z.union([catalogEntryDetailSchema, moveResultSchema]),
        init,
      );
    },

    setGrant: async (id, grantee, level) =>
      request(
        transport,
        `/api/catalogs/${id}/acl/${encodeURIComponent(grantee)}`,
        catalogAclResponseSchema,
        jsonInit(
          "PUT",
          checkedBody(aclSetSchema, { level }, "catalogs.setGrant received an invalid grant level"),
        ),
      ),

    revokeGrant: async (id, grantee) =>
      request(
        transport,
        `/api/catalogs/${id}/acl/${encodeURIComponent(grantee)}`,
        catalogAclResponseSchema,
        { method: "DELETE" },
      ),

    usernames: async () => {
      const res = await request(
        transport,
        "/api/users",
        z.object({ data: z.array(z.object({ username: z.string() })) }),
      );
      return res.data.map((u) => u.username);
    },
  };
}

// ── Session ─────────────────────────────────────────────────────────

export type { AuthConfig };
export type {
  BarcodeLookupResponse,
  ItemRefreshResponse,
  ItemsSyncResponse,
};

export interface SessionRequests {
  /** Log in; the API sets the session cookie. */
  login(username: string, password: string): Promise<SessionIdentity>;
  logout(): Promise<void>;
  /** The current identity, `null` when there is no valid session. Offline-safe. */
  me(): Promise<SessionIdentity | null>;
  /** Public auth discovery; `null` when it cannot be read. Anonymous-safe. */
  authConfig(): Promise<AuthConfig | null>;
}

const loginBodySchema = z.object({ username: z.string(), password: z.string() });

function createSessionRequests(transport: Transport): SessionRequests {
  return {
    login: async (username, password) =>
      request(
        transport,
        "/api/auth/login",
        loginResponseSchema,
        jsonInit(
          "POST",
          checkedBody(loginBodySchema, { username, password }, "session.login received invalid credentials"),
        ),
      ),

    logout: async () =>
      requestVoid(transport, "/api/auth/logout", {
        method: "POST",
        headers: { "content-type": "application/json" },
      }),

    me: async () => {
      try {
        return await request(transport, "/api/me", meResponseSchema);
      } catch {
        return null;
      }
    },

    authConfig: async () => {
      try {
        return await request(transport, "/api/auth/config", authConfigSchema);
      } catch {
        return null;
      }
    },
  };
}

// ── Search ──────────────────────────────────────────────────────────

export interface SearchRequests {
  /** Tolerant ranked search; the server computes paths and scores. */
  search(query: string, limit?: number): Promise<SearchResult[]>;
  /**
   * Gallery photos. With a query the server restricts the gallery to the
   * containers the tolerant search matches; without one it lists everything.
   */
  photos(query?: string): Promise<PhotosResponse>;
}

function createSearchRequests(transport: Transport): SearchRequests {
  return {
    search: async (query, limit = 20) => {
      const res = await request(
        transport,
        `/api/search?q=${encodeURIComponent(query)}&limit=${limit}`,
        searchResponseSchema,
      );
      return res.results;
    },

    photos: async (query = "") => {
      const q = query.trim();
      return request(
        transport,
        q ? `/api/photos?q=${encodeURIComponent(q)}` : "/api/photos",
        photosResponseSchema,
      );
    },
  };
}

// ── The six groups ──────────────────────────────────────────────────

export interface Requests {
  containers: ContainersRequests;
  items: ItemsRequests;
  media: MediaRequests;
  catalogs: CatalogsRequests;
  session: SessionRequests;
  search: SearchRequests;
}

/** Build the six capability groups over one transport. */
export function createRequests(transport: Transport): Requests {
  return {
    containers: createContainersRequests(transport),
    items: createItemsRequests(transport),
    media: createMediaRequests(transport),
    catalogs: createCatalogsRequests(transport),
    session: createSessionRequests(transport),
    search: createSearchRequests(transport),
  };
}

// ── Request internals ───────────────────────────────────────────────

/**
 * Validate an outgoing body once, in the capability that sends it, and
 * serialise it the same way everywhere. A caller that passes a shape the
 * shared request schema rejects gets an `InvalidInputError`, not an `ApiError`:
 * no request was made.
 */
function checkedBody<S extends z.ZodTypeAny>(schema: S, input: unknown, message: string): string {
  const parsed = schema.safeParse(input);
  if (!parsed.success) throw new InvalidInputError(message, { cause: parsed.error });
  return JSON.stringify(parsed.data);
}

/** A JSON write with an already-serialised body. */
function jsonInit(method: string, body: string): RequestInit {
  return { method, headers: { "content-type": "application/json" }, body };
}

/** The bare `{ version }` body used by file and tree writes. */
const versionBodySchema = z.object({ version: z.number().int().nonnegative() });

// ── Response reading ────────────────────────────────────────────────

/** A content type that carries JSON: application/json or any `+json`. */
function isJsonContentType(contentType: string | null): boolean {
  if (!contentType) return false;
  const mediaType = contentType.split(";")[0]!.trim().toLowerCase();
  return mediaType === "application/json" || mediaType.endsWith("+json");
}

/**
 * Best-effort JSON for a failing response. A non-JSON or malformed body is
 * `undefined` so the status always surfaces as an ApiError, never as a
 * SyntaxError from `res.json()`.
 */
async function readErrorBody(res: Response): Promise<unknown> {
  if (!isJsonContentType(res.headers.get("content-type"))) return undefined;
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

/**
 * JSON for a successful response. A malformed JSON body is an invalid
 * response, not a missing one; a non-JSON content type yields `undefined`, so
 * the schema decides whether the payload is acceptable.
 */
async function readSuccessBody(res: Response, path: string): Promise<unknown> {
  if (!isJsonContentType(res.headers.get("content-type"))) return undefined;
  try {
    return await res.json();
  } catch (cause) {
    throw new InvalidResponseError(path, { cause });
  }
}

/** Send through the transport, turning a rejection into UnreachableError. */
async function send(
  transport: Transport,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  try {
    return await transport(path, init);
  } catch (cause) {
    throw new UnreachableError(path, { cause });
  }
}
